import React, { useMemo } from 'react';
import { Activity, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import FootballMobileGameCard from './FootballMobileGameCard';
import { EmptyState, LoadingSpinner } from "../../../../../components/shared/loading";

const MobileFootballGamesView = ({
  fixtures,
  loading,
  error,
  pagination,
  onPageChange,
  t,
  onRetry,
  onViewOdds,
  isGameSelected,
  isGameFrozen,
  onToggleSelection,
  onViewPrediction,
  getGamePrediction
}) => {
  const paginatedFixtures = useMemo(() => {
    if (!pagination) return fixtures;
    const startIndex = (pagination.currentPage - 1) * pagination.perPage;
    return fixtures.slice(startIndex, startIndex + pagination.perPage);
  }, [fixtures, pagination]);

  const handlePageChange = (direction) => {
    if (!pagination) return;
    const nextPage = direction === 'next' ? pagination.currentPage + 1 : pagination.currentPage - 1;
    onPageChange(Math.max(1, Math.min(nextPage, pagination.totalPages)));
  };

  return (
    <section className="space-y-4">
      <div className="border border-white/10 bg-white/[0.02]">
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-200">
              {t('Currently showing')}
            </p>
            <h2 className="flex items-center gap-2 text-lg font-black uppercase tracking-tight text-white">
              {t('Games')} ({fixtures.length})
            </h2>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-200">
              {t('Page')} {pagination.currentPage} {t('of')} {pagination.totalPages}
            </span>
          )}
        </div>

        {loading ? (
          <LoadingSpinner message={t('Loading fixtures...')} />
        ) : error ? (
          <EmptyState
            title={t('Failed to load fixtures')}
            description={error}
            action={
              onRetry ? (
                <button
                  onClick={onRetry}
                  className="rounded-lg bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-300 transition hover:bg-emerald-500/30"
                >
                  {t('Try again')}
                </button>
              ) : null
            }
          />
        ) : fixtures.length === 0 ? (
          <EmptyState
            title={t('No fixtures found')}
            description={t('Try selecting a different date')}
            icon={Calendar}
          />
        ) : (
          <div className="space-y-3 p-4">
            {paginatedFixtures.map((fixture, index) => {
              const id = fixture?.fixture?.id || `fixture-${index}`;
              return (
                <FootballMobileGameCard
                  key={id}
                  fixture={fixture}
                  onViewOdds={onViewOdds}
                  isSelected={isGameSelected?.(fixture)}
                  isFrozen={isGameFrozen?.(fixture?.fixture?.id)}
                  onToggleSelection={() => onToggleSelection?.(fixture)}
                  onViewPrediction={onViewPrediction}
                  prediction={getGamePrediction ? getGamePrediction(id) : null}
                  showPredictionButton={true}
                />
              );
            })}
          </div>
        )}

        {!loading && fixtures.length > 0 && pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 border-t border-white/5 px-4 py-3">
            <button
              onClick={() => handlePageChange('prev')}
              disabled={pagination.currentPage === 1}
              className="flex items-center gap-1 border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-200 transition-all disabled:opacity-40"
            >
              <ChevronLeft size={14} /> {t('Prev')}
            </button>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-gray-200">
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange('next')}
              disabled={pagination.currentPage === pagination.totalPages}
              className="flex items-center gap-1 border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-200 transition-all disabled:opacity-40"
            >
              {t('Next')} <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MobileFootballGamesView;
